import React from 'react';
import {AccessorKeyColumnDef, DisplayColumnDef} from '@tanstack/react-table';
import {TagRead, TagType} from '../../schemas';
import {DocFieldType, TableItemForGridFormat} from './data';
import {GridView} from '../../contexts/NavContext/nav-context';
import {CodeRender, ExplanationRender, IdRender} from './special-cell-rendering';
import {ENABLE_ENTITY_TAGS} from '../../feature_flags';

/**
 * Column metadata for header filter -> backend mapping.
 */
export interface ColumnMeta {
    docField?: DocFieldType;
    filterPlaceholder?: string;
    hideIn?: GridView[];
}

export type CellValue = string | number | boolean | string[] | Date | null;

export type KeyColDef = AccessorKeyColumnDef<TableItemForGridFormat, CellValue> & { meta?: ColumnMeta };
export type DispColDef = DisplayColumnDef<TableItemForGridFormat, CellValue> & { meta?: ColumnMeta };
export type GridColumnDef = KeyColDef | DispColDef;

type KeyName = Extract<keyof TableItemForGridFormat, string>;

// Columns that support server-side sorting (must match backend ALLOWED_SORT_FIELDS)
const SORTABLE_COLUMNS = new Set([
    'upload_time', 'file_created_date', 'file_modified_date',
    'content_date_start', 'content_date_end', 'title', 'file_name',
    'file_size_in_bytes', 'custodian', 'type', 'file_type',
    'body_char_count',
]);

/**
 * Helper to create a standard text column.
 */
export function createStringColumn(
    key: keyof TableItemForGridFormat,
    header: string,
    meta: ColumnMeta,
    opts?: { hidden?: boolean; size?: number }
): GridColumnDef {
    return {
        id: String(key),
        accessorKey: key as KeyName,
        header,
        size: opts?.size ?? 220,
        enableSorting: SORTABLE_COLUMNS.has(String(key)),
        meta,
        cell: (ctx) => {
            const v = ctx.getValue() as string | null;
            return <div className="whitespace-pre-wrap text-sm text-gray-800">{v ?? ''}</div>;
        },
        enableHiding: true,
    };
}

/**
 * Helper to create a date column with formatted display.
 */
export function createDateColumn(
    key: keyof TableItemForGridFormat,
    header: string,
    meta: ColumnMeta,
    opts?: { size?: number }
): GridColumnDef {
    return {
        id: String(key),
        accessorKey: key as KeyName,
        header,
        size: opts?.size ?? 140,
        enableSorting: SORTABLE_COLUMNS.has(String(key)),
        meta,
        cell: (ctx) => {
            const v = ctx.getValue() as string | Date | null;
            if (!v) return <div className="text-sm text-gray-400">—</div>;
            const date = typeof v === 'string' ? new Date(v) : v;
            const formatted = date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
            return <div className="text-sm text-gray-800">{formatted}</div>;
        },
        enableHiding: true,
    };
}

/**
 * Helper to create an array column (e.g., authors).
 */
export function createArrayColumn(
    key: keyof TableItemForGridFormat,
    header: string,
    meta: ColumnMeta,
    opts?: { size?: number }
): GridColumnDef {
    return {
        id: String(key),
        accessorKey: key as KeyName,
        header,
        size: opts?.size ?? 180,
        enableSorting: false,
        meta,
        cell: (ctx) => {
            const v = ctx.getValue() as string[] | null;
            if (!v || v.length === 0) return <div className="text-sm text-gray-400">—</div>;
            return <div className="line-clamp-3 text-sm text-gray-800">{v.join(', ')}</div>;
        },
        enableHiding: true,
    };
}

/**
 * Helper to create a body preview column (truncated).
 */
export function createBodyPreviewColumn(meta: ColumnMeta, opts?: { size?: number }): GridColumnDef {
    return {
        id: 'body',
        accessorKey: 'body' as KeyName,
        header: 'Preview',
        size: opts?.size ?? 260,
        enableSorting: false,
        meta,
        cell: (ctx) => {
            const v = ctx.getValue() as string | null;
            if (!v) return <div className="text-sm text-gray-400">—</div>;
            // Truncate to ~150 chars for preview
            const truncated = v.length > 150 ? v.slice(0, 150) + '…' : v;
            return <div className="line-clamp-3 text-sm text-gray-700">{truncated}</div>;
        },
        enableHiding: true,
    };
}

/**
 * Get all document grid columns.
 */
export function getDocumentColumns(gridView?: GridView): GridColumnDef[] {
    return [
        // ID column (always first, non-hideable)
        {
            id: 'id',
            accessorKey: 'id',
            header: ' ',
            size: 120,
            enableSorting: false,
            enableColumnFilter: false,
            enableHiding: false,
            meta: {},
            cell: ({row}) => <IdRender row={row} gridView={gridView}/>,
        },

        // Title
        createStringColumn('title', 'Title', {docField: 'title', }),

        // Participants
        {
            id: 'participants',
            accessorKey: 'participants',
            header: 'Participants',
            size: 180,
            enableSorting: false,
            meta: {docField: 'participants', hideIn: [GridView.MyDashboard]},
            cell: (ctx) => {
                const persons = ctx.getValue() as Array<{name?: string}> | null;
                if (!persons || persons.length === 0) return <div className="text-sm text-gray-400">—</div>;
                const names = persons.map(p => p.name).filter(Boolean).join(', ');
                return <div className="line-clamp-3 text-sm text-gray-800">{names || '—'}</div>;
            },
            enableHiding: true,
        },

        // Document Type
        createStringColumn('type', 'Type', {
            docField: 'type',
            hideIn: [GridView.MyDashboard],
        }, {size: 100}),

        // Email fields
        createStringColumn('from_email', 'From', {
            docField: 'from_email',
            hideIn: [GridView.Default, GridView.MyDashboard],
        }),
        createStringColumn('to_email', 'To', {
            docField: 'to_email',
            hideIn: [GridView.Default, GridView.MyDashboard],
        }),
        createStringColumn('x_from', 'X-From', {
            docField: 'x_from',
            hideIn: [GridView.Default, GridView.MyDashboard],
        }),
        createStringColumn('x_to', 'X-To', {
            docField: 'x_to',
            hideIn: [GridView.Default, GridView.MyDashboard],
        }),
        createStringColumn('x_cc', 'CC', {
            docField: 'x_cc',
            hideIn: [GridView.Default, GridView.MyDashboard],
        }),
        createStringColumn('x_bcc', 'BCC', {
            docField: 'x_bcc',
            hideIn: [GridView.Default, GridView.MyDashboard],
        }),

        // Summary
        createStringColumn('summary', 'Summary', {
            docField: 'summary',
            hideIn: [GridView.MyDashboard],
        }),

        // Body Preview
        createBodyPreviewColumn({docField: 'body', filterPlaceholder: 'Filter body…', hideIn: [GridView.Default, GridView.MyDashboard]}),

        // Date columns
        createDateColumn('content_date_start', 'Content Date Start', {
            docField: 'content_date_start',
            hideIn: [GridView.MyDashboard],
        }),
        createDateColumn('content_date_end', 'Content Date End', {
            docField: 'content_date_end',
            hideIn: [GridView.MyDashboard],
        }),

        createDateColumn('file_created_date', 'File Created', {
            docField: 'file_created_date',
            hideIn: [GridView.Default, GridView.MyDashboard],
        }),
        createDateColumn('file_modified_date', 'File Modified', {
            docField: 'file_modified_date',
            hideIn: [GridView.Default, GridView.MyDashboard],
        }),

        // File Name
        createStringColumn('file_name', 'File Name', {docField: 'file_name', }),

        // Document Length (pages for PDFs, word count for text)
        {
            id: 'body_char_count',
            accessorKey: 'body_char_count' as KeyName,
            header: 'Length',
            size: 90,
            enableSorting: true,
            meta: {},
            cell: ({row}) => {
                const pages = row.original.page_count;
                const words = row.original.word_count;
                if (pages) {
                    return <div className="text-sm text-gray-800 text-right">{pages} pg</div>;
                }
                if (words) {
                    const display = words >= 1000 ? `${(words / 1000).toFixed(1)}k` : String(words);
                    return <div className="text-sm text-gray-800 text-right">{display} words</div>;
                }
                return <div className="text-sm text-gray-400 text-right">—</div>;
            },
            enableHiding: true,
        },

        // Code columns
        {
            id: 'document_requests',
            accessorKey: 'document_requests',
            header: 'Document Requests',
            size: 260,
            enableSorting: false,
            meta: {hideIn: [GridView.MyDashboard]},
            cell: ({row}) => <CodeRender original={row.original} tagType={TagType.REQUEST_SET}/>,
        },
        {
            id: 'codes',
            accessorKey: 'codes',
            header: 'Codes',
            size: 260,
            enableSorting: false,
            meta: {hideIn: [GridView.MyDashboard]},
            cell: ({row}) => <CodeRender original={row.original} tagType={TagType.TAG} tagFilter="codes"/>,
        },
        {
            id: 'transcript_codes',
            accessorKey: 'transcript_codes',
            header: 'Testimony',
            size: 220,
            enableSorting: false,
            meta: {hideIn: [GridView.MyDashboard, GridView.Default]},
            cell: ({row}) => <CodeRender original={row.original} tagType={TagType.TAG} tagFilter="transcript"/>,
        },
        // Entities column (feature-flagged)
        ...(ENABLE_ENTITY_TAGS ? [{
            id: 'entities',
            accessorKey: 'entities',
            header: 'Entities',
            size: 260,
            enableSorting: false,
            meta: {hideIn: [GridView.MyDashboard, GridView.Default]},
            cell: ({row}: {row: {original: TableItemForGridFormat}}) => <CodeRender original={row.original} tagType={TagType.ENTITY}/>,
        }] : []),
    ];
}

/**
 * Compute initial column visibility based on grid view.
 */
export function computeInitialVisibility(columns: GridColumnDef[], view?: GridView): Record<string, boolean> {
    const initial: Record<string, boolean> = {};

    const getColId = (col: GridColumnDef): string => {
        const withId = col as { id?: string };
        if (withId.id) return String(withId.id);
        const withKey = col as { accessorKey?: string };
        return withKey.accessorKey ?? '';
    };

    columns.forEach((col) => {
        const id = getColId(col);
        if (!id) return;

        // ID column is always visible
        if (id === 'id') {
            initial[id] = true;
            return;
        }

        const meta = (col as { meta?: ColumnMeta }).meta;
        const shouldHide = !!meta?.hideIn && !!view && meta.hideIn.includes(view);
        if (shouldHide) {
            initial[id] = false;
        }
    });

    return initial;
}

/**
 * Create a dynamic explanation column for a specific tag.
 */
export function createExplanationColumn(tag: TagRead): GridColumnDef {
    return {
        id: `explanation_${tag.id}`,
        accessorKey: 'id' as KeyName, // We use id as accessor but render custom content
        header: `${tag.tag_name} (Explanation)`,
        size: 220,
        enableSorting: false,
        meta: {},
        cell: ({row}) => (
            <ExplanationRender
                original={row.original}
                tagId={tag.id}
                tagName={tag.tag_name}
            />
        ),
        enableHiding: true,
    };
}

/**
 * Generate explanation columns for the given tag IDs.
 */
export function getExplanationColumns(
    tagIds: Set<string>,
    tagMap: Map<string, TagRead>
): GridColumnDef[] {
    const columns: GridColumnDef[] = [];

    for (const tagId of tagIds) {
        const tag = tagMap.get(tagId);
        if (tag) {
            columns.push(createExplanationColumn(tag));
        }
    }

    return columns;
}